import { drawRowHighlights } from '@/shared/lib/highlighter/highlightDrawing';
import { loadImageElementFromUrl } from '@/shared/lib/highlighter/imageLoaders';

// Keep Korean text and parentheses (e.g. "경동제약(주)") but strip characters
// that are illegal in file names on macOS/Windows.
function sanitizeFileNamePart(value, fallback = '') {
  const cleaned = String(value ?? '')
    .trim()
    .replaceAll(/[\\/:*?"<>|]/g, '')
    .replaceAll(/\s+/g, ' ')
    .trim();
  return cleaned || fallback;
}

function imageBaseName(imageName) {
  return String(imageName || 'image').replace(/\.[^.]+$/, '');
}

// File name: `YYYYMM_{병원명}_{제약사명}`. 병원명 is not returned by the OCR
// service, so fall back to the image file name.
function buildExportFileName({ yyyymm, hospitalName, imageName, companyName }) {
  const ym = sanitizeFileNamePart(yyyymm, 'NA');
  const hospital = sanitizeFileNamePart(hospitalName, imageBaseName(imageName));
  const company = sanitizeFileNamePart(companyName, 'company');
  return `${ym}_${hospital}_${company}.png`;
}

function canvasToBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('PNG blob 생성에 실패했습니다.'));
        return;
      }
      resolve(blob);
    }, 'image/png');
  });
}

function downloadBlob(blob, fileName) {
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(blobUrl);
}

async function renderExportCanvas({
  imageSrc,
  rows,
  selectedRowIds,
  companyName,
  companyColors,
}) {
  const image = await loadImageElementFromUrl(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;
  const context = canvas.getContext('2d');

  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  drawRowHighlights({
    context,
    rows,
    selectedRowIds: new Set(selectedRowIds),
    companyColors,
    width: canvas.width,
    height: canvas.height,
    filterCompanyName: companyName,
  });

  return canvas;
}

function hexToRgbaExport(hexColor, alpha) {
  const v = (hexColor || '#fa541c').replace('#', '');
  const n =
    v.length === 3
      ? v
          .split('')
          .map((c) => c + c)
          .join('')
      : v;
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// "다른 이름으로 저장": render the current view exactly as shown — selected OCR
// bands (with move/delete edits already applied to `rows`) plus manual bands —
// and download with a caller-supplied file name.
export async function exportCurrentView({
  imageSrc,
  rows,
  selectedRowIds,
  manualBands = [],
  companyColors,
  fileName,
}) {
  const image = await loadImageElementFromUrl(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;
  const context = canvas.getContext('2d');
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  drawRowHighlights({
    context,
    rows,
    selectedRowIds: new Set(selectedRowIds),
    companyColors,
    width: canvas.width,
    height: canvas.height,
  });

  manualBands.forEach((band) => {
    const v = band.vertices;
    if (!v || v.length < 3) return;
    context.beginPath();
    context.moveTo(v[0].x * canvas.width, v[0].y * canvas.height);
    for (let i = 1; i < v.length; i += 1) {
      context.lineTo(v[i].x * canvas.width, v[i].y * canvas.height);
    }
    context.closePath();
    context.setLineDash([]);
    context.fillStyle = hexToRgbaExport(band.color || '#fa541c', 0.16);
    context.fill();
    context.strokeStyle = hexToRgbaExport(band.color || '#fa541c', 0.55);
    context.lineWidth = 1;
    context.stroke();
  });

  const blob = await canvasToBlob(canvas);
  const safeName = sanitizeFileNamePart(fileName, 'highlight');
  downloadBlob(blob, `${safeName}.png`);
}

export async function exportCompanyHighlight({
  imageSrc,
  imageName,
  yyyymm,
  hospitalName,
  rows,
  selectedRowIds,
  companyName,
  companyColors,
}) {
  const selectedRows = rows.filter(
    (row) =>
      row.rawPharmaCompanyName === companyName &&
      selectedRowIds.includes(row.rowId),
  );

  if (!selectedRows.length) {
    throw new Error('해당 제약사에서 선택된 row가 없습니다.');
  }

  const canvas = await renderExportCanvas({
    imageSrc,
    rows: selectedRows,
    selectedRowIds,
    companyName,
    companyColors,
  });
  const blob = await canvasToBlob(canvas);
  downloadBlob(
    blob,
    buildExportFileName({ yyyymm, hospitalName, imageName, companyName }),
  );
}

export async function exportAllCompanyHighlights({
  imageSrc,
  imageName,
  yyyymm,
  hospitalName,
  rows,
  selectedRowIds,
  companyColors,
}) {
  const companyNames = [
    ...new Set(
      rows
        .filter((row) => selectedRowIds.includes(row.rowId))
        .map((row) => row.rawPharmaCompanyName),
    ),
  ];

  for (const companyName of companyNames) {
    await exportCompanyHighlight({
      imageSrc,
      imageName,
      yyyymm,
      hospitalName,
      rows,
      selectedRowIds,
      companyName,
      companyColors,
    });
  }

  return companyNames.length;
}
